import React from 'react';
import styles from '../assets/checklistGroup.module.css';
import FileUpload from './FileUpload';

const ChecklistGroup = ({
  title,
  sections,
  locations,
  onLocationAdd,
  onLocationRemove,
  newLocation,
  setNewLocation,
  data,
  onChange,
  isMetropolitan = false, // MTVH uses different labels and simplified checklist
  showLocations = true, // MTVH hides the locations section
  ticketTypes = [], // Services for MTVH ticket type dropdown
  monthlyInspectionResponses = null, // Canned responses for MTVH notes dropdown
  onOfflineSave = null // Buffer checklist files when offline (passed to FileUpload)
}) => {
  // Labels for condition options
  const goodLabel = "Good";
  const badLabel = isMetropolitan ? "Requires Attention" : "Not Good";
  const handleChecklistChange = (section, item, field, value) => {
    if (typeof onChange === 'function') {
      onChange(section, item, field, value);
    } else {
      console.error('❌ [ChecklistGroup] onChange is not a function:', onChange);
    }
  };

  // Get filtered responses for a section based on condition (good/bad)
  // Default to 'good' if condition is not set (undefined/empty)
  const getFilteredResponses = (sectionKey, condition, itemName) => {
    if (!monthlyInspectionResponses) return [];

    // Default to 'good' if condition is undefined or empty (since Good is selected by default)
    const responseType = (condition === '1' || condition === undefined || condition === '') ? 'good' : 'bad';
    const responses = monthlyInspectionResponses[responseType] || [];

    // Filter responses based on section key
    return responses.filter(response => {
      // If no filter, show for all sections
      if (!response.filter) return true;
      // Check if this section is in the filter list
      return response.filter.includes(sectionKey);
    }).map(response => ({
      ...response,
      // Replace {type} placeholder with item name
      displayMessage: response.message.replace('{type}', itemName)
    }));
  };

  // Check if selected response requires additional notes
  const responseRequiresNotes = (selectedResponse, sectionKey, condition, itemName) => {
    if (!selectedResponse || !monthlyInspectionResponses) return false;
    const responses = getFilteredResponses(sectionKey, condition, itemName);
    const found = responses.find(r => r.displayMessage === selectedResponse);
    return found?.notes_required || false;
  };

  const renderChecklistSection = (sectionKey, sectionData) => {
    const sectionItems = data[sectionKey] || {};
    
    return (
      <div key={sectionKey} className={styles.checklistSection}>
        <div className={styles.sectionHeader}>{sectionData.title}:</div>
        <div className={styles.checklistTable}>
          <div className={styles.tableHeader}>
            <div className={styles.headerName}>Name</div>
            <div className={styles.headerCondition}>Condition</div>
            <div className={styles.headerNotes}>Notes</div>
            <div className={styles.headerFile}>File (max. 100MB)</div>
          </div>
          
          {sectionData.items.map((item) => {
            const itemData = sectionItems[item] || {};
            
            // Debug: Log the type of itemData.files
            if (itemData.files !== undefined) {
              console.log(`🔍 [DEBUG] ${sectionKey}.${item}.files:`, {
                value: itemData.files,
                type: typeof itemData.files,
                isString: typeof itemData.files === 'string',
                isArray: Array.isArray(itemData.files)
              });
            }
            
            return (
              <div key={item} className={styles.checklistRow}>
                <div className={styles.itemName}>{item}</div>
                
                <div className={styles.itemCondition}>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name={`${sectionKey}_${item}_condition`}
                        value="1"
                        checked={itemData.condition === '1' || itemData.condition === undefined || itemData.condition === ''}
                        onChange={(e) => handleChecklistChange(sectionKey, item, 'condition', e.target.value)}
                        className={styles.radioInput}
                      />
                      {goodLabel}
                    </label>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name={`${sectionKey}_${item}_condition`}
                        value="0"
                        checked={itemData.condition === '0'}
                        onChange={(e) => {
                          handleChecklistChange(sectionKey, item, 'condition', e.target.value);
                          // For MTVH: Clear notes and ticket type when switching to "Requires Attention"
                          // User must explicitly select a response
                          if (isMetropolitan) {
                            handleChecklistChange(sectionKey, item, 'notes_response', '');
                            handleChecklistChange(sectionKey, item, 'notes', '');
                            handleChecklistChange(sectionKey, item, 'notes_additional', '');
                            handleChecklistChange(sectionKey, item, 'ticket_type', '');
                          }
                        }}
                        className={styles.radioInput}
                      />
                      {badLabel}
                    </label>
                  </div>
                </div>
                
                <div className={styles.itemNotes}>
                  {/* MTVH uses dropdown with canned responses, standard uses textarea */}
                  {isMetropolitan && monthlyInspectionResponses ? (
                    <div className={styles.notesDropdownContainer}>
                      <select
                        value={itemData.notes_response || ''}
                        onChange={(e) => {
                          handleChecklistChange(sectionKey, item, 'notes_response', e.target.value);
                          // If "Choose response" (empty) is selected, clear notes
                          if (!e.target.value) {
                            handleChecklistChange(sectionKey, item, 'notes', '');
                            handleChecklistChange(sectionKey, item, 'notes_additional', '');
                            return;
                          }
                          // If response doesn't require additional notes, set notes to the response
                          const responses = getFilteredResponses(sectionKey, itemData.condition, item);
                          const selectedResp = responses.find(r => r.displayMessage === e.target.value);
                          if (selectedResp && !selectedResp.notes_required) {
                            handleChecklistChange(sectionKey, item, 'notes', e.target.value);
                          }
                        }}
                        className={styles.notesSelect}
                      >
                        <option value="">Choose response</option>
                        {getFilteredResponses(sectionKey, itemData.condition, item).map((response, idx) => (
                          <option key={idx} value={response.displayMessage}>
                            {response.displayMessage}
                          </option>
                        ))}
                      </select>
                      {/* Show additional notes textarea when response requires notes */}
                      {responseRequiresNotes(itemData.notes_response, sectionKey, itemData.condition, item) && (
                        <textarea
                          value={itemData.notes_additional || ''}
                          onChange={(e) => {
                            handleChecklistChange(sectionKey, item, 'notes_additional', e.target.value);
                            // Combine response with additional notes
                            handleChecklistChange(sectionKey, item, 'notes', `${itemData.notes_response} ${e.target.value}`);
                          }}
                          className={styles.notesTextarea}
                          rows={2}
                          placeholder="Add additional notes..."
                        />
                      )}
                    </div>
                  ) : (
                    <textarea
                      value={itemData.notes || ''}
                      onChange={(e) => handleChecklistChange(sectionKey, item, 'notes', e.target.value)}
                      className={styles.notesTextarea}
                      rows={3}
                    />
                  )}
                </div>

                <div className={styles.itemFile}>
                  <FileUpload
                    onChange={(file) => handleChecklistChange(sectionKey, item, 'files', file)}
                    disabled={itemData.condition !== '0'}
                    accept="image/*"
                    maxSize={100 * 1024 * 1024} // 100MB
                    folder={`monthly-inspection-files/properties/${window.location.pathname.split('/')[2]}/reports/mir`}
                    placeholder="Choose file..."
                    onOfflineSave={onOfflineSave}
                    existingFile={itemData.files}
                    existingFileName={itemData.files && typeof itemData.files === 'string' ? itemData.files.split('/').pop() : ''}
                  />
                  {/* Ticket type dropdown for MTVH properties */}
                  {isMetropolitan && ticketTypes.length > 0 && (
                    <div className={styles.ticketTypeContainer}>
                      <div className={styles.ticketTypeLabel}>Ticket type</div>
                      <select
                        className={styles.ticketTypeSelect}
                        value={itemData.ticket_type || ''}
                        onChange={(e) => handleChecklistChange(sectionKey, item, 'ticket_type', e.target.value)}
                        disabled={itemData.condition !== '0'}
                      >
                        <option value="">Please select</option>
                        {ticketTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderLocationSection = (location) => {
    const locationKey = `location[${location}]`;
    const locationItems = data.location?.[location] || {};
    
    const locationSectionData = {
      title: location,
      items: [
        'Door', 'Wall', 'Ceiling', 'Window(s)', 'Floor', 'Light(s)',
        'Fire Extinguisher(s)', 'Plug socket(s)', 'Other'
      ]
    };
    
    // Default location cannot be removed (matches Laravel behavior)
    const isDefaultLocation = location === 'Communal area - Location';

    return (
      <div key={location} className={styles.checklistSection}>
        <div className={styles.sectionHeader}>
          {location}:
          {!isDefaultLocation && (
            <button
              type="button"
              onClick={() => onLocationRemove(location)}
              className={styles.removeLocationBtn}
              title="Remove location"
            >
              ×
            </button>
          )}
        </div>
        <div className={styles.checklistTable}>
          <div className={styles.tableHeader}>
            <div className={styles.headerName}>Name</div>
            <div className={styles.headerCondition}>Condition</div>
            <div className={styles.headerNotes}>Notes</div>
            <div className={styles.headerFile}>File (max. 100MB)</div>
          </div>
          
          {locationSectionData.items.map((item) => {
            const itemData = locationItems[item] || {};
            
            // Debug: Log the type of itemData.files
            if (itemData.files !== undefined) {
              console.log(`🔍 [DEBUG] location.${location}.${item}.files:`, {
                value: itemData.files,
                type: typeof itemData.files,
                isString: typeof itemData.files === 'string',
                isArray: Array.isArray(itemData.files)
              });
            }
            
            return (
              <div key={item} className={styles.checklistRow}>
                <div className={styles.itemName}>{item}</div>
                
                <div className={styles.itemCondition}>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name={`${locationKey}_${item}_condition`}
                        value="1"
                        checked={itemData.condition === '1' || itemData.condition === undefined || itemData.condition === ''}
                        onChange={(e) => handleChecklistChange('location', `${location}.${item}`, 'condition', e.target.value)}
                        className={styles.radioInput}
                      />
                      {goodLabel}
                    </label>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name={`${locationKey}_${item}_condition`}
                        value="0"
                        checked={itemData.condition === '0'}
                        onChange={(e) => handleChecklistChange('location', `${location}.${item}`, 'condition', e.target.value)}
                        className={styles.radioInput}
                      />
                      {badLabel}
                    </label>
                  </div>
                </div>
                
                <div className={styles.itemNotes}>
                  <textarea
                    value={itemData.notes || ''}
                    onChange={(e) => handleChecklistChange('location', `${location}.${item}`, 'notes', e.target.value)}
                    className={styles.notesTextarea}
                    rows={3}
                  />
                </div>
                
                <div className={styles.itemFile}>
                  <FileUpload
                    onChange={(file) => handleChecklistChange('location', `${location}.${item}`, 'files', file)}
                    disabled={itemData.condition !== '0'}
                    accept="image/*"
                    maxSize={100 * 1024 * 1024} // 100MB
                    folder={`monthly-inspection-files/properties/${window.location.pathname.split('/')[2]}/reports/mir`}
                    placeholder="Choose file..."
                    onOfflineSave={onOfflineSave}
                    existingFile={itemData.files}
                    existingFileName={itemData.files && typeof itemData.files === 'string' ? itemData.files.split('/').pop() : ''}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.checklistGroup}>
      <div className={styles.groupTitle}>{title}</div>
      
      {/* Static sections */}
      {Object.entries(sections).map(([sectionKey, sectionData]) => 
        renderChecklistSection(sectionKey, sectionData)
      )}
      
      {/* Dynamic locations - hidden for MTVH */}
      {showLocations && (
        <>
          <div className={styles.locationsContainer}>
            {locations.map((location) => renderLocationSection(location))}
          </div>

          {/* Add new location */}
          <div className={styles.addLocationSection}>
            <div className={styles.addLocationInput}>
              <input
                type="text"
                placeholder="Add Communal Area"
                value={newLocation}
                onChange={(e) => setNewLocation(e.target.value)}
                className={styles.locationInput}
              />
              <button
                type="button"
                onClick={onLocationAdd}
                className={styles.addLocationBtn}
              >
                <span className={styles.addIcon}>+</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChecklistGroup; 